import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useMutation, useQueryClient} from 'react-query'
import {AlertBoostrapButtons, Toast} from '../../../../../../../_metronic/partials/widgets'
import {transferProject} from '../../../core/_requests'
import {cloneProject, deleteProject, openProject, toProjectTemplate} from '../../../core/_requests'
import {useQueryResponse} from '../../../core/QueryResponseProvider'
import {getApiErrorMessage, ID, QUERIES} from '../../../../../../../_metronic/helpers'
import {MenuComponent} from '../../../../../../../_metronic/assets/ts/components'
import {ProjectCloseModal} from '../../../overview/project-close-modal/ProjectCloseModal'
import {Link, useNavigate} from 'react-router-dom'
import {Team} from '../../../../../../../_metronic/partials/modals/team/core/_models'
import {EditProjectTeamsModal} from '../../../overview/edit-project-teams-modal/EditProjectTeamsModal'
import {ProjectPortalInviteModal} from '../../../overview/components/ProjectPortalInviteModal'
import {ProjectShareMenuItem} from '../../../shared/ProjectShareMenuItem'
import {
  CloneProjectModal,
  type CloneProjectPayload,
} from '../../../clone-project-modal/CloneProjectModal'
import Swal from 'sweetalert2'
import {useHasModule} from '../../../../../auth'
import {getAllClients} from '../../../../../clients/components/core/_requests'
import axios from 'axios'
import {
  ProjectSummaryExportModal,
  ProjectSummaryExportOptions,
} from '../../../overview/project-summary-export-modal/ProjectSummaryExportModal'

type Props = {
  id: ID
  closed_at?: string | null
  teams?: Team[]
  projectName?: string
  clientId?: number | string
  clientName?: string
  projectType?: number
}

const ProjectListActions: FC<Props> = ({
  id,
  closed_at,
  teams,
  projectName,
  clientId,
  clientName,
  projectType,
}) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {query} = useQueryResponse()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showEditProjectTeamsModal, setShowEditProjectTeamsModal] = useState(false)
  const [showPortalInviteModal, setShowPortalInviteModal] = useState(false)
  const [showSummaryExportModal, setShowSummaryExportModal] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const hasProjectTemplatesModule = useHasModule('modelos-de-projetos')

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const confirmProjectDelete = async () => {
    AlertBoostrapButtons.fire({
      title: intl.formatMessage({id: 'ALERT.LABEL.ARE_YOU_SURE'}),
      html: `<h5>${intl.formatMessage({id: 'ALERT.LABEL.IT_CAN_NOT_BE_UNDONE_PROJECT'})}<h5>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({id: 'GENERAL.YES_EXCLUDE'}),
      cancelButtonText: intl.formatMessage({id: 'GENERAL.CANECEL'}),
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        return await deleteItem.mutateAsync()
      }
    })
  }

  const deleteItem = useMutation(() => deleteProject(id), {
    onSuccess: (response) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_DELETE_ITEM'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.PROJECT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
      queryClient.invalidateQueries([`${QUERIES.PROJECTS_LIST}-${query}`])
      queryClient.invalidateQueries(QUERIES.DASHBOARD_PROJECTS)
    },
  })

  const openItem = useMutation(() => openProject(id), {
    onSuccess: (response) => {
      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_OPEN_ITEM'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.PROJECT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
      queryClient.invalidateQueries([`${QUERIES.PROJECTS_LIST}-${query}`])
      queryClient.invalidateQueries(QUERIES.DASHBOARD_PROJECTS)
    },
    onError: (error: unknown) => {
      const apiMessage =
        typeof error === 'object' && error !== null && 'response' in error
          ? (error as any).response?.data?.message
          : undefined

      Toast.fire({
        icon: 'error',
        title: apiMessage || intl.formatMessage({id: 'ALERT.LABEL.ERROR'}),
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    },
  })

  const toTemplateMutation = useMutation(
    (opts: {includeOpenTasks: boolean; includeDeliveredTasks: boolean}) =>
      toProjectTemplate(id, {
        include_open_tasks: opts.includeOpenTasks,
        include_delivered_tasks: opts.includeDeliveredTasks,
      }),
    {
      onSuccess: (response) => {
        const templateId = response?.data?.project_template_id
        Toast.fire({
          icon: 'success',
          title: 'Modelo criado com sucesso!',
          customClass: {popup: 'bg-light-success', title: 'text-success'},
        })
        if (templateId) {
          navigate(`/modelos-projetos/${templateId}`)
        }
      },
      onError: () => {
        Toast.fire({
          icon: 'warning',
          title: intl.formatMessage({id: 'ALERT.LABEL.ERROR'}),
          customClass: {popup: 'bg-light-danger', title: 'text-danger'},
        })
      },
    })

  const cloneMutation = useMutation(
    (opts: {
      name: string
      clientId: number
      includeOpenTasks: boolean
      includeDeliveredTasks: boolean
    }) =>
      cloneProject(id, opts.name, opts.clientId, {
        include_open_tasks: opts.includeOpenTasks,
        include_delivered_tasks: opts.includeDeliveredTasks,
      }),
    {
      onSuccess: (response) => {
        const projectId = response?.data?.project_id
        setShowCloneModal(false)
        Toast.fire({
          icon: 'success',
          title: 'Projeto clonado com sucesso!',
          customClass: {popup: 'bg-light-success', title: 'text-success'},
        })
        if (projectId) {
          navigate(`/projects/${projectId}`)
        }
        queryClient.invalidateQueries([`${QUERIES.PROJECTS_LIST}-${query}`])
        queryClient.invalidateQueries(QUERIES.DASHBOARD_PROJECTS)
      },
      onError: () => {
        Toast.fire({
          icon: 'warning',
          title: intl.formatMessage({id: 'ALERT.LABEL.ERROR'}),
          customClass: {popup: 'bg-light-danger', title: 'text-danger'},
        })
      },
    })

  const confirmToTemplate = async () => {
    AlertBoostrapButtons.fire({
      title: 'Transformar em modelo',
      html: `<h5>Vamos criar um modelo de projeto com as tarefas e subtarefas deste projeto.</h5>
        <p class="text-muted fs-7 mb-3">Desmarque o que não quiser copiar para o modelo.</p>
        <div class="form-check mb-2 text-start">
          <input class="form-check-input" type="checkbox" id="swal-list-tpl-include-open" checked>
          <label class="form-check-label" for="swal-list-tpl-include-open">Tarefas abertas</label>
        </div>
        <div class="form-check text-start">
          <input class="form-check-input" type="checkbox" id="swal-list-tpl-include-delivered" checked>
          <label class="form-check-label" for="swal-list-tpl-include-delivered">Tarefas entregues</label>
        </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: intl.formatMessage({id: 'GENERAL.CANECEL'}),
      reverseButtons: true,
      preConfirm: () => {
        const openEl = document.getElementById('swal-list-tpl-include-open') as HTMLInputElement | null
        const deliveredEl = document.getElementById('swal-list-tpl-include-delivered') as HTMLInputElement | null
        const includeOpenTasks = openEl?.checked ?? true
        const includeDeliveredTasks = deliveredEl?.checked ?? true
        if (!includeOpenTasks && !includeDeliveredTasks) {
          // @ts-ignore
          AlertBoostrapButtons.showValidationMessage(
            'Marque ao menos uma opção: tarefas abertas ou tarefas entregues.'
          )
          return false
        }
        return {includeOpenTasks, includeDeliveredTasks}
      },
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        return await toTemplateMutation.mutateAsync(result.value as {includeOpenTasks: boolean; includeDeliveredTasks: boolean})
      }
    })
  }

  const confirmClone = async () => setShowCloneModal(true)

  const openModalCloseProject = (project_id: string | number) => {
    setShowModal(true)
  }

  const openEditProjectTeamsModal = (project_id: number) => {
    setShowEditProjectTeamsModal(true)
  }

  const transferItem = useMutation((newClientId: number) => transferProject(id, newClientId), {
    onSuccess: () => {
      Toast.fire({
        icon: 'success',
        title: 'Projeto movido com sucesso!',
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
      queryClient.invalidateQueries([`${QUERIES.PROJECTS_LIST}-${query}`])
      queryClient.invalidateQueries(QUERIES.DASHBOARD_PROJECTS)
    },
    onError: (error: any) => {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Erro ao mover projeto',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    },
  })

  const handleMoveProject = async () => {
    try {
      const clientsResponse = await getAllClients()
      const clients = clientsResponse.data || []

      const currentClientId = clientId ? Number(clientId) : undefined
      const sortedClients = clients
        .filter((c: any) => c.id !== currentClientId)
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'pt-BR'))

      const clientOptions = sortedClients.reduce((acc: any, item: any) => {
        acc[`c_${item.id}`] = item.name
        return acc
      }, {})

      if (Object.keys(clientOptions).length === 0) {
        Toast.fire({
          icon: 'warning',
          title: 'Não há outros clientes disponíveis para movimentação',
          customClass: {
            popup: 'bg-light-warning',
            title: 'text-warning',
          },
        })
        return
      }

      const result = await Swal.fire({
        title: 'Mover projeto',
        html: `
          <p class="text-start mb-3">Selecione o novo cliente para este projeto:</p>
          <p class="text-start text-muted small mb-0">Ao mover este projeto, as tarefas associadas também serão movidas.</p>
        `,
        input: 'select',
        inputOptions: clientOptions,
        inputPlaceholder: 'Selecione um cliente',
        showCancelButton: true,
        confirmButtonText: 'Mover projeto',
        cancelButtonText: intl.formatMessage({id: 'GENERAL.CANECEL'}),
        confirmButtonColor: '#009ef7',
        cancelButtonColor: '#6c757d',
        inputValidator: (value) => {
          if (!value) {
            return 'Você precisa selecionar um cliente!'
          }
        },
      })

      if (result.isConfirmed && result.value) {
        const newClientId = parseInt(result.value.replace(/^c_/, ''), 10)
        await transferItem.mutateAsync(newClientId)
      }
    } catch (error) {
      Toast.fire({
        icon: 'error',
        title: 'Erro ao buscar clientes',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    }
  }

  const handleExportSummaryWithOptions = async (options: ProjectSummaryExportOptions) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_THEME_API_URL}/api/relatorio/projeto/${id}/resumo/pdf`,
        {
          include_blocks: options.include_blocks,
          include_private_milestones: options.include_private_milestones,
        }
      )

      const {uuidFile} = response.data ?? {}
      if (uuidFile) {
        window.open(`${process.env.REACT_APP_THEME_API_URL}/storage/pdf/${uuidFile}`, '_blank')
        Toast.fire({
          icon: 'success',
          title: 'Resumo exportado com sucesso',
          customClass: {
            popup: 'bg-light-success',
            title: 'text-success',
          },
        })
      } else {
        Toast.fire({
          icon: 'warning',
          title: 'Não foi possível gerar o resumo',
          customClass: {
            popup: 'bg-light-warning',
            title: 'text-warning',
          },
        })
      }
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Erro ao exportar resumo',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    }
  }

  return (
    <>
      <div className='actions'>
        <a
          href='#'
          className='text-hover-primary dash-menu-trigger-hover d-flex'
          data-kt-menu-trigger='click'
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
        >
          <i className='bi bi-three-dots-vertical fs-1 text-dark'></i>
        </a>
        {/* begin::Menu */}
        <div
          className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary dash-menu-hover-green fw-bold fs-7 w-auto py-4'
          data-kt-menu='true'
        >
          {/* begin::Menu item */}
          <div className='menu-item px-3'>
            <a
              className='menu-link px-3'
              data-kt-user-table-filter='edit_row'
              onClick={() => openEditProjectTeamsModal(id!)}
            >
              {intl.formatMessage({id: 'PROJECT.ACTIONS.VISIBILITY_SETTINGS'})}
            </a>
          </div>

          <ProjectShareMenuItem onShare={() => setShowPortalInviteModal(true)} />

          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={() => setShowSummaryExportModal(true)}>
              Exportar resumo
            </a>
          </div>

          <div className='menu-item px-3'>
            {!closed_at ? (
              <a className='menu-link px-3' onClick={() => openModalCloseProject(id!)}>
                {intl.formatMessage({id: 'PROJECT.ACTIONS.CLOSE'})}
              </a>
            ) : (
              <a className='menu-link px-3' onClick={async () => await openItem.mutateAsync()}>
                {intl.formatMessage({id: 'PROJECT.ACTIONS.OPEN'})}
              </a>
            )}
          </div>

          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={handleMoveProject}>
              Mover projeto
            </a>
          </div>
          {/* end::Menu item */}

          {/* begin::Menu item */}
          {/* <div className='menu-item px-3'>
                <a className='menu-link px-3' onClick={() => {}}>
                {intl.formatMessage({id: 'PROJECT.ACTIONS.TIMILINE'})}
                </a>
            </div> */}
          {/* end::Menu item */}

          {/* begin::Menu item */}
          <div className='menu-item px-3'>
            <Link to={`/projects/${id}/tasks`} className='menu-link px-3'>
              {intl.formatMessage({id: 'PROJECT.ACTIONS.TASKS'})}
            </Link>
          </div>
          {/* end::Menu item */}

          <div className='separator my-2'></div>

          {hasProjectTemplatesModule && (
            <div className='menu-item px-3'>
              <a className='menu-link px-3' onClick={confirmToTemplate}>
                Transformar em modelo
              </a>
            </div>
          )}

          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={confirmClone}>
              Clonar projeto
            </a>
          </div>

          {/* begin::Menu item */}
          <div className='menu-item px-3'>
            <a
              className='menu-link px-3'
              data-kt-user-table-filter='delete_row'
              onClick={confirmProjectDelete}
            >
              {intl.formatMessage({id: 'PROJECT.ACTIONS.DELETE'})}
            </a>
          </div>
          {/* end::Menu item */}
        </div>
        {/* end::Menu */}
      </div>
      {/* 
        //@ts-ignore */}
      <ProjectCloseModal show={showModal} project_id={id} handleClose={() => setShowModal(false)} />

      {/* @ts-ignore */}
      <EditProjectTeamsModal
        show={showEditProjectTeamsModal}
        project_id={id}
        teams={teams}
        handleClose={() => setShowEditProjectTeamsModal(false)}
      />

      {/* Modal do Link do Cliente */}
      {/* Modal de Convites do Portal do Cliente */}
      <ProjectPortalInviteModal
        show={showPortalInviteModal}
        projectId={id ? (typeof id === 'string' ? parseInt(id) : id) : undefined}
        projectName={projectName}
        clientId={clientId ? (typeof clientId === 'string' ? parseInt(clientId) : Number(clientId)) : undefined}
        clientName={clientName}
        handleClose={() => setShowPortalInviteModal(false)}
      />
      <ProjectSummaryExportModal
        show={showSummaryExportModal}
        onHide={() => setShowSummaryExportModal(false)}
        projectId={Number(id)}
        projectType={projectType ?? 1}
        onExport={handleExportSummaryWithOptions}
      />
      
      <CloneProjectModal
        show={showCloneModal}
        onHide={() => setShowCloneModal(false)}
        projectName={projectName}
        currentClientId={clientId ? Number(clientId) : undefined}
        currentClientName={clientName}
        isSubmitting={cloneMutation.isLoading}
        onSubmit={async (payload: CloneProjectPayload) => {
          await cloneMutation.mutateAsync(payload)
        }}
      />
    </>
  )
}

export {ProjectListActions}
